import { DataTable, useColumns } from "@/components/data-table";
import { DataFetcher } from "@/components/display";
import { useFilters } from "@/components/filters";
import { ListPage } from "@/components/list-page";
import { ConfirmDialog, Dialog, ModalContextExtractor, ModalHeader, ModalValueExtractor, useOpenModal } from "@/components/modal";
import useHasPermisos from "@/features/auth/hooks/useHasPermisos";
import CsvExportDialog from "@/features/export/components/csv-export-dialog";
import PrintExportDialog from "@/features/export/components/print-dialog-obs";
import XlsxExportDialog from "@/features/export/components/xlsx-export-dialog";
import { AddArticuloTransferenciaMutation } from "@/features/inventario/articulo-transferencias/api/add-articulo-transferencia";
import { DeleteArticuloTransferenciaMutation } from "@/features/inventario/articulo-transferencias/api/delete-articulo-transferencia";
import { getAllArticuloTransferencias, useGetAllArticuloTransferenciasQuery } from "@/features/inventario/articulo-transferencias/api/get-all-articulo-transferencias";
import { GetArticuloTransferenciaQuery } from "@/features/inventario/articulo-transferencias/api/get-articulo-transferencia";
import ArticuloTransferenciaForm, { articuloTransferenciaFormSchema } from "@/features/inventario/articulo-transferencias/components/articulo-transferencia-form";
import ArticuloTransferenciaView from "@/features/inventario/articulo-transferencias/components/view-articulo-transferencia";
import { formatDate, formatString } from "@/shared/formats";
import { notify } from "@/shared/notification";
import { Permiso } from "@/shared/types/permiso";
import { ArticuloTransferencia, ArticuloTransferenciaTipo } from "@/shared/types/articulo-transferencia";
import { SchemaFormHook } from "@/components/form";

export default function ArticuloTransferenciasPage() {
    const openModal = useOpenModal();

    const hasWritePermiso = useHasPermisos(Permiso.WriteArticuloTransferencias);

    const filterData = useFilters({
        filters: [
            { type: "dateRange", label: "Fecha", fromName: "desde", toName: "hasta" },
            {
                type: "select", label: "Tipo", name: "tipo", options: [
                    { label: "Envío", value: ArticuloTransferenciaTipo.Envio },
                    { label: "Recepción", value: ArticuloTransferenciaTipo.Recepcion }
                ]
            },
            { type: "pagination" }
        ]
    });

    const fetchData = useGetAllArticuloTransferenciasQuery(filterData.filterData);

    const columnData = useColumns<ArticuloTransferencia>({
        columns: [
            { key: "fecha", header: "Fecha", render: row => formatDate(row.fecha), width: "auto" },
            { key: "tipo", header: "Tipo", render: row => row.tipo == ArticuloTransferenciaTipo.Envio ? "Envío" : "Reccepción" },
            { key: "creadoPor", header: "Creado Por", render: row => formatString(row.creadoPor), show: false },
            { key: "creacion", header: "Fecha de Creación", render: row => formatDate(row.fechaCreacion), align: "center", show: false },
            { key: "modificadoPor", header: "Modificado Por", render: row => formatString(row.modificadoPor), show: false },
            { key: "modificacion", header: "Última Modificación", render: row => formatDate(row.fechaModificacion), align: "center", show: false }
        ],
        key: "articulo-transferencias"
    });

    return <ListPage>
        <ListPage.Header>
            <ListPage.Header.Actions title="Transferencias de Artículo">
                <ListPage.Header.Actions.FilterButton />
                <ListPage.Header.Actions.ColumnButton columnData={columnData} />
                {hasWritePermiso && <ListPage.Header.Actions.AddButton onClick={() => openModal("add")} />}
                <ListPage.Header.Actions.Divider />
                <ListPage.Header.Actions.ExportButton onClick={() => openModal("print")}>
                    <ListPage.Header.Actions.ExportButton.Print onClick={() => openModal("print")} />
                    <ListPage.Header.Actions.ExportButton.Csv onClick={() => openModal("csv")} />
                    <ListPage.Header.Actions.ExportButton.Xlsx onClick={() => openModal("xlsx")} />
                </ListPage.Header.Actions.ExportButton>
            </ListPage.Header.Actions>
            <ListPage.Header.Filters filterData={filterData} />
        </ListPage.Header>
        <DataTable flex={1}>
            <DataTable.Content
                rows={fetchData.data?.result}
                columns={[
                    ...columnData.columns,
                    {
                        header: "Acción", width: "auto", align: "center", render: row => <DataTable.EntryOption>
                            <DataTable.EntryOption.ViewButton onClick={() => openModal("view", row.id)} />
                            {hasWritePermiso && <DataTable.EntryOption.DeleteButton disabled={row.protegido} title="Anular" onClick={() => openModal("delete", row)} />}
                        </DataTable.EntryOption>
                    }
                ]}
                fit="flex"
                loading={fetchData.isFetching}
                error={fetchData.error}
            />
            <DataTable.Pagination filterData={filterData} queryResult={fetchData} />
        </DataTable>

        <Dialog code="add" size="lg" disableEscapeKeyDown fullHeight>
            <ModalHeader>Agregar Nueva Transferencia</ModalHeader>
            <ModalContextExtractor>
                {modal => <AddArticuloTransferenciaMutation>
                    {mutation => <SchemaFormHook
                        schema={articuloTransferenciaFormSchema}
                        onSubmit={async ({ value }) => {
                            await mutation.mutateAsync(value);
                            notify("La transferencia se ha agregado correctamente.");
                        }}
                        submitOnce
                        closeModalOnSubmit
                    >
                        {form => <ArticuloTransferenciaForm
                            margin={3}
                            form={form}
                            onCancel={modal.handleClose}
                            flex={1}
                        />}
                    </SchemaFormHook>}
                </AddArticuloTransferenciaMutation>}
            </ModalContextExtractor>
        </Dialog>

        <Dialog code="view" size="md" fullHeight>
            <ModalHeader>Ver Transferencia</ModalHeader>
            <ModalValueExtractor>
                {articuloTransferenciaId => <GetArticuloTransferenciaQuery id={articuloTransferenciaId}>
                    {query => <DataFetcher query={query} flex={1}>
                        {articuloTransferencia => <ArticuloTransferenciaView articuloTransferencia={articuloTransferencia} />}
                    </DataFetcher>}
                </GetArticuloTransferenciaQuery>}
            </ModalValueExtractor>
        </Dialog>

        <ConfirmDialog code="delete">
            <ModalValueExtractor>
                {articuloTransferencia => <DeleteArticuloTransferenciaMutation id={articuloTransferencia.id}>
                    {mutation => <ConfirmDialog.Content
                        title={`Anular Transferencia ${formatDate(articuloTransferencia.fecha)}`}
                        description={`¿Estás seguro que quieres anular la transferencia ${formatDate(articuloTransferencia.fecha)}?`}
                        onConfirm={async () => {
                            await mutation.mutateAsync({});
                            notify(`La transferencia "${formatDate(articuloTransferencia.fecha)}" se ha anulado correctamente.`, { variant: "success" });
                        }}
                    />}
                </DeleteArticuloTransferenciaMutation>}
            </ModalValueExtractor>
        </ConfirmDialog>

        <PrintExportDialog
            code="print"
            columns={columnData.columns}
            filterData={filterData}
            fetchFn={getAllArticuloTransferencias}
            defaultFileTitle="Transferencias de Artículos"
        />

        <CsvExportDialog
            code="csv"
            columns={columnData.columns}
            filterData={filterData}
            fetchFn={getAllArticuloTransferencias}
            defaultFileTitle="Transferencias de Artículos"
        />

        <XlsxExportDialog
            code="xlsx"
            columns={columnData.columns}
            filterData={filterData}
            fetchFn={getAllArticuloTransferencias}
            defaultFileTitle="Transferencias de Artículos"
        />
    </ListPage>
}